import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { environment } from '../../environments/environment';
import { CustomerdatabaseService } from './customerdatabase.service';
import { applyTeamType } from './organigram-graph-normalize';
import { toBackendNodeWrite, OrgWriteNodeType } from './organigram-node-write';
import {
  WorkflowConnection, ForEachSpec, RepeatUntilSpec, ConnectionPatch, chainTail, forEachOf, repeatUntilOf
} from './workflow-connection.model';
import { newWorkflowId } from '../mona-app/workflow-builder/org-trigger-editor/org-trigger-editor.model';
import { AuthService } from './auth.service';

// Node types defined by the organigram backend. `department` and `division` are the grouping
// layers the backend is adding above teams (root -> department -> division -> team -> member).
//
// 'trigger' became a real backend type on 2026-08-05 and is now written like any other node - it
// used to be rejected in both directions, which is what the test-node block below existed for.
// 'pipeline' followed on 2026-09-03: a node that POINTS AT a data pipeline (PipelineData is only
// pipeline_id). It is a WORKFLOW STEP: it sits only under a trigger (canPlaceUnder enforces that)
// and is appended to the trigger's step chain (appendWorkflowStep); the backend runs it after the
// step before it. Until the backend has its execute handler, a run that reaches one fails that visit.
export type OrganigramNodeType = 'human' | 'agent' | 'team' | 'department' | 'division' | 'trigger' | 'pipeline';
//response from backend has this type.
export type OrganigramNodeTeamType = 'department' | 'division';

// Full node from GET /nodes/{id}. `data` shape depends on `type` (see organigram-node-write.ts for
// the write-side models, * = required):
//   human:   { email*, uid*, name*, phone_number?, location_id? }
//   agent:   { agent_id*, summary?, location_id?, structured_output? }
//   team:    { type* (division|department|team), structured_output? }
//   trigger: { user_id*, account_id?, allow_sensitive_actions? }
//   pipeline: { pipeline_id* }   - the data_pipelines document it stands for; nothing else is stored
// structured_output (agent + team) is a field name -> FieldInfo map (see organigram-structured-output.ts);
// once set, the node's execution `response` is that JSON serialized to text, so a reader parses it
// (parseStructuredResponse) rather than showing it as prose. `data` stays Record<string, any> here
// because a single node type is not known at this shape - the callers narrow it.
export interface OrganigramNode {
  id: string;
  parent_id: string | null;
  children_ids: string[];
  name: string;
  description: string;
  goals: string;
  type: OrganigramNodeType;
  data: Record<string, any>;
  active?: boolean;
  created_at?: string;
}

// Lightweight node from the graph list (GET /nodes/) - id, name, active.
// `type` is optional until the backend adds it to the list response.
export interface OrganigramNodeSummary {
  id: string;
  name: string;
  active: boolean;
  type?: OrganigramNodeType;
  team_type?: OrganigramNodeTeamType;
}

// Directed edge from the graph list: source is the parent, target is the child.
export interface OrganigramEdge {
  source: string;
  target: string;
}

export interface OrganigramGraph {
  nodes: OrganigramNodeSummary[];
  edges: OrganigramEdge[];
}

// Client for the new organigram backend. Endpoints are mounted under
// {CLOUD_URL_BASE}/organigram/{organigram_id}/...  This is a NEW file on purpose:
// the legacy WorkflowApiService / CustomerdatabaseService are left untouched.
@Injectable({ providedIn: 'root' })
export class OrganigramApiService {
  // The organigram backend is deployed PER PROJECT, and a Cloud Run host carries the GCP project
  // NUMBER - so the host is the customer's own, not a constant. It used to be hardcoded to one
  // project's number, which meant every customer's organigram went to that one project.
  //
  // The number comes from the customer's Firebase config (customerDB.db), where `messagingSenderId`
  // IS the project number - the same value, under Firebase's name for it. Nothing else the client
  // holds carries it: `projectId` is the human-readable id ('mona-ai-pwa-firebase') and there is no
  // client-side mapping between the two.
  // The backend is deployed per project and NOT every project is in the same region, so the region
  // is looked up by project number rather than being a constant. It was hardcoded to europe-west2,
  // which is right for one project and wrong for all the others.
  //
  // TEMPORARY. This map is a stopgap until the backend moves behind the api.mona-ai.cloud load
  // balancer, at which point the client stops needing to know where it runs and both this and
  // projectNumber go away. Until then a customer deployed outside the default region needs an entry
  // here and a frontend release - which is exactly why it is not the permanent answer.
  //
  // Listing only the EXCEPTIONS, so onboarding a customer in the usual region costs nothing.
  private static readonly DEFAULT_REGION = 'europe-west3';
  private static readonly REGION_BY_PROJECT: Record<string, string> = {
    // The one project deployed to europe-west2, which is what this service used to assume for
    // everyone. Every other project falls through to the default below.
    //for now its europe server, coz we havent deployed all to EU-west2 yet. //TODO
    '71707359619': 'europe-west3' 
  };

  // Falling back to the default is safe in a way that guessing a project NUMBER is not: the number
  // is still in the hostname, so a wrong region yields a host that does not resolve rather than a
  // request that reaches somebody else's project. It fails; it cannot leak.
  private get backendRegion(): string {
    return OrganigramApiService.REGION_BY_PROJECT[this.projectNumber]
      || OrganigramApiService.DEFAULT_REGION;
  }

  // GETTERS, not fields. `db` is not set until connectDB runs, so a field initialised at
  // construction would bake in an empty number for the whole session - the same too-early trap the
  // account id has.
  private get baseUrl(): string {
    return `https://organigram-backend-${this.projectNumber}.${this.backendRegion}.run.app/organigram`;
  }

  // Host root (baseUrl without the /organigram router prefix). The cross-org aggregates GET /sessions
  // and GET /pending are mounted here, NOT under /organigram (confirmed against the backend's OpenAPI).
  private get rootUrl(): string {
    return this.baseUrl.replace(/\/organigram$/, '');
  }

  // Deliberately NO fallback to a literal. A wrong-but-plausible number would point a customer's
  // organigram at somebody else's project, which is worse in every way than a request that fails -
  // so a missing one yields a host that cannot resolve, and says why in the console.
  private warnedNoProject = false;
  private get projectNumber(): string {
    const number = this.customerDB.db?.messagingSenderId || '';
    if (!number && !this.warnedNoProject) {
      this.warnedNoProject = true;
      console.error(
        '[OrganigramApi] no project number yet - the customer database has not connected, or its '
        + 'Firebase config carries no messagingSenderId. Organigram calls will not resolve.'
      );
    }
    return number;
  }

  // The human-readable project id ('mona-ai-pwa-firebase'), as opposed to projectNumber above. Both
  // come from the customer's Firebase config; the Cloud Run host wants the number, cloud FUNCTIONS
  // want the id. Exposed here because this service already owns the question "which project is this
  // customer", and a second reader of customerDB.db would be a second answer to it.
  get projectId(): string {
    return this.customerDB.db?.projectId || '';
  }

  // ---- Selected organigram ----------------------------------------------------------------
  // Which organigram the app is currently looking at. The backend shares its id space with the
  // legacy Process Manager: workflow-builder.page mirrors every newly created company into this
  // backend under the SAME id and stores it in localStorage['processConfigId']. Reading it here
  // keeps the organigram views on the company the user picked in the switcher, rather than the
  // hardcoded 'default' they used before. The legacy page stays the owner of the selection and
  // reports changes via setCurrentId/clearCurrentId; views outside the page (the app-level chat
  // dock) have no other way to learn it.
  // NO fallback organigram. Nothing selected means nothing selected - it used to mean the id
    // 'default', which loaded whatever organigram happened to be under that name and presented it
    // as the user's own. A wrong org chart is far worse than an empty one: it looks like data.
    //
  private static readonly ID_STORAGE_KEY = 'processConfigId';
  private readonly currentId = new BehaviorSubject<string>(
    localStorage.getItem(OrganigramApiService.ID_STORAGE_KEY) || ''
  );

  constructor(
    private http: HttpClient,
    private customerDB: CustomerdatabaseService,
    private authService: AuthService
  ) {}

  // The selected organigram, or '' when there is none. Callers MUST check: an empty id builds
  // `/organigram//nodes/`, which is a request to nowhere.
  get currentOrganigramId(): string { return this.currentId.value; }

  // Emits on every selection change so views can reload. Fires immediately with the current id.
  get currentOrganigramId$(): Observable<string> { return this.currentId.asObservable(); }

  // Report the company the legacy page just selected or saved.
  setCurrentId(id: string): void {
    if (!id) { this.clearCurrentId(); return; }
    localStorage.setItem(OrganigramApiService.ID_STORAGE_KEY, id);
    if (id !== this.currentId.value) { this.emit(id); }
  }

  // Report that the legacy page dropped the selection (new / blank organization).
  clearCurrentId(): void {
    localStorage.removeItem(OrganigramApiService.ID_STORAGE_KEY);
    if (this.currentId.value !== '') { this.emit(''); }
  }

  // Subscribers run synchronously on next(), so a throwing one would propagate back into the
  // caller - the legacy workflow-builder page, which reports the selection mid-method and would
  // skip the rest of it (e.g. closing the org switcher). A view failing to reload must never
  // break the page that told it to.
  private emit(id: string): void {
    try {
      this.currentId.next(id);
    } catch (err) {
      console.error('[OrganigramApi] organigram-id subscriber failed:', err);
    }
  }

  // BOTH headers go on every route. The nodes endpoints used to be sent without X-User-Id, on the
  // strength of openapi declaring only X-Account-Id on them - but the backend reads the user off the
  // raw request, which never shows up in the schema, and the README is explicit that every external
  // route wants a non-blank X-User-Id. Omitting it answers a bare `500 Internal Server Error`
  // (text/plain, no body), which reads exactly like the service being down.
  //
  // A blank X-Account-Id is NOT the same problem and is legitimate here: single-tenant projects pass
  // the account through unchecked, so an empty one is expected rather than a symptom.
  // THE single source of truth for the signed-in identity in the organigram. customerDB's in-memory
  // id lags or stays blank on /organigram (connectDB fires the auth signal before setUserStorage
  // populates it, and setUserStorage can be skipped or throw), so both resolvers fall back to the auth
  // service's localStorage-backed value. Every organigram/account-scoped read must go through these
  // rather than reading customerDB directly - that scatters the fallback and it gets forgotten (it was,
  // three times: the headers, the chat dock, and createTriggerNode). Change the rule here, once.
  resolveUserId(): string {
    return this.customerDB.returnUserId() || this.authService.returnUserId() || '';
  }
  resolveAccountId(): string {
    return this.customerDB.getAccountId() || this.authService.returnAccountId() || '';
  }

  // The signed-in user's EMAIL, lowercased, or '' when it cannot be established. Separate from
  // resolveUserId() because it answers a different backend: an agent's viewerRights block is keyed by
  // email, while every organigram header and grant is keyed by uid, and the two are not convertible
  // without a lookup.
  //
  // The chain is the one the agents area already uses (agent-setting.page.ts:499-506,
  // mona-agent-chat.page.ts:7482-7489), not a new invention. It deliberately does NOT include
  // customerDB.getUserEmail(): minimalAgentsLogin calls authService.saveEmail() but never
  // customerDB.setUserEmail(), so that one is blank on the in-SPA hop into /organigram while being
  // correct on a direct load - an intermittent wrong answer is worse than none. It also must never
  // include returnCustomerAdminEmailAdress() (applicants.page.ts:123 does, and that is a bug worth
  // not copying): that is the ACCOUNT ADMIN's address, so it would identify every user in the account
  // as the admin and hand them the admin's agent permissions.
  //
  // '' means UNKNOWN, never DENY - see agentAccess() in workflow-graph/logic/agent-access.ts.
  resolveUserEmail(): string {
    try {
      const email = this.authService.getCurrentUserFromFirebaseAuth()?.email || this.authService.returnUser() || '';
      return String(email).trim().toLowerCase();
    } catch {
      return '';
    }
  }

  private buildHeaders(includeUser: boolean): HttpHeaders {
    let headers = new HttpHeaders().set('X-Account-Id', this.resolveAccountId());
    if (includeUser) {
      headers = headers.set('X-User-Id', this.resolveUserId());
    }
    return headers;
  }

  // ---- Organigram (company) document CRUD ------------------------------------------------
  // These operate on the top-level organigram/{id} doc itself, not its nodes. `team_count` is
  // computed on read.

  // GET /organigram  - every organigram in the account scope, each with its computed team_count.
  listOrganigrams(): Promise<OrganigramResponse[]> {
    return this.http.get<OrganigramResponse[]>(this.baseUrl, { headers: this.buildHeaders(true) }).toPromise();
  }

  // POST /organigram  - create the top-level company doc (nodes are added separately, 201).
  // `id` is optional: when non-blank it becomes the doc id, otherwise the backend mints one.
  // `name` is required and must not be blank; an existing id is a 409, never an overwrite.
  // Note: exactly `/organigram`, no trailing slash (unlike the /nodes/ collection routes).
  createOrganigram(body: CreateOrganigramBody): Promise<OrganigramResponse> {
    return this.http.post<OrganigramResponse>(this.baseUrl, body, { headers: this.buildHeaders(true) }).toPromise();
  }

  // GET /organigram/{organigram_id}  - one organigram doc with its computed team_count (404 if none).
  getOrganigram(organigramId: string): Promise<OrganigramResponse> {
    const url = `${this.baseUrl}/${organigramId}`;
    return this.http.get<OrganigramResponse>(url, { headers: this.buildHeaders(true) }).toPromise();
  }

  // PATCH /organigram/{organigram_id}  - partial update of the company doc; returns the updated
  // document. Only the keys present in the body are written, so one field can be edited without
  // resending the rest - but an explicit null CLEARS that field, so callers must omit what they do
  // not mean to touch rather than pass a falsy value on. The id is immutable and not part of the body.
  updateOrganigram(organigramId: string, patch: UpdateOrganigramBody): Promise<OrganigramResponse> {
    const url = `${this.baseUrl}/${organigramId}`;
    return this.http.patch<OrganigramResponse>(url, patch, { headers: this.buildHeaders(true) }).toPromise();
  }

  // DELETE /organigram/{organigram_id}  - ARCHIVE the company document (204, no body). Despite the
  // verb this is a soft delete: the backend sets `archived = true` and KEEPS the nodes / requests /
  // processes subcollections. The organigram drops out of listOrganigrams either way, so to a caller
  // reloading the list it is indistinguishable from a real delete - which is why the pilot UI still
  // calls it "delete". The permanent counterpart is DELETE /organigram/{id}/purge, which this client
  // deliberately does not expose. There is no un-archive yet: POST /organigram/{id}/restore is in
  // the backend README but is NOT in the deployed OpenAPI, so do not build on it.
  //
  // Nothing comes back to patch a local list with, so callers reload after it resolves.
  deleteOrganigram(organigramId: string): Promise<void> {
    const url = `${this.baseUrl}/${organigramId}`;
    return this.http.delete<void>(url, { headers: this.buildHeaders(true) }).toPromise();
  }

  // GET /organigram/{organigram_id}/nodes/  - the whole organigram as nodes + edges.
  // nodes endpoints only validate X-Account-Id, so X-User-Id is omitted here.
  getGraph(organigramId: string): Promise<OrganigramGraph> {
    const url = `${this.baseUrl}/${organigramId}/nodes/`;
    return this.http.get<OrganigramGraph>(url, { headers: this.buildHeaders(true) })
      .toPromise()
      .then(response => applyTeamType(response));
  }

  // GET /organigram/{organigram_id}/nodes/{node_id}  - a single node with all its fields.
  getNode(organigramId: string, nodeId: string): Promise<OrganigramNode> {
    const url = `${this.baseUrl}/${organigramId}/nodes/${nodeId}`;
    return this.http.get<OrganigramNode>(url, { headers: this.buildHeaders(true) }).toPromise();
  }

  // POST /organigram/{id}/nodes/  - create a node. First node ever becomes the root
  // (must NOT declare parent_id); every later node must declare parent_id.
  // `type`/`data` are the UI's, and toBackendNodeWrite translates them into the backend's own
  // model - a department is a team node carrying data.type, and HumanData must be complete.
  createNode(organigramId: string, body: CreateNodeBody): Promise<{ id: string }> {
    const url = `${this.baseUrl}/${organigramId}/nodes/`;
    // The first node becomes the root and must NOT declare parent_id at all. Sending an explicit
    // null is not the same as omitting the key, so drop it rather than pass a falsy value on:
    // callers derive the parent from the graph and legitimately have nothing to send for a root.
    const mapped = toBackendNodeWrite(body);
    const { parent_id, ...withoutParent } = mapped;
    const payload = parent_id ? mapped : withoutParent;
    return this.http.post<{ id: string }>(url, payload, { headers: this.buildHeaders(true) }).toPromise();
  }

  // PATCH /organigram/{id}/nodes/{nodeId}  - partial update; only provided fields are written (204).
  // Same UI-to-backend translation as createNode. A patch without `type` is sent as-is: `data` is
  // replaced wholesale, so those callers must merge into the node's current data object.
  updateNode(organigramId: string, nodeId: string, patch: UpdateNodeBody): Promise<void> {
    const url = `${this.baseUrl}/${organigramId}/nodes/${nodeId}`;
    return this.http.patch<void>(url, toBackendNodeWrite(patch), { headers: this.buildHeaders(true) }).toPromise();
  }

  // DELETE /organigram/{id}/nodes/{nodeId}  - root cannot be deleted; the subtree is orphaned/inactive.
  deleteNode(organigramId: string, nodeId: string): Promise<{ inactivated_node_ids: string[] }> {
    const url = `${this.baseUrl}/${organigramId}/nodes/${nodeId}`;
    return this.http.delete<{ inactivated_node_ids: string[] }>(url, { headers: this.buildHeaders(true) }).toPromise();
  }

  // PUT /organigram/{id}/nodes/{nodeId}/parent/replace  - reparent a node that already has a parent (204).
  replaceParent(organigramId: string, nodeId: string, parentId: string): Promise<void> {
    const url = `${this.baseUrl}/${organigramId}/nodes/${nodeId}/parent/replace`;
    return this.http.put<void>(url, { parent: parentId }, { headers: this.buildHeaders(true) }).toPromise();
  }

  // PUT /organigram/{id}/nodes/{nodeId}/parent/add  - attach a parent to an ORPHANED node and
  // reactivate its whole subtree (the counterpart to deleteNode, which orphans a subtree).
  addParent(organigramId: string, nodeId: string, parentId: string): Promise<{ reactivated_nodes_ids: string[] }> {
    const url = `${this.baseUrl}/${organigramId}/nodes/${nodeId}/parent/add`;
    return this.http.put<{ reactivated_nodes_ids: string[] }>(url, { parent: parentId }, { headers: this.buildHeaders(true) }).toPromise();
  }

  // POST /organigram/{id}/requests/start_message  - start a task at a node; returns request_id.
  // requests endpoints require X-User-Id.
  // `sessionId` continues an existing chat session: the backend replays that session's routing
  // context for the entry node, so a follow-up question keeps the conversation. Blank/omitted
  // starts a new session - its id comes back in the response and must be carried into the next call.
  startMessage(organigramId: string, entryNodeId: string, userMessage: string, sessionId = ''): Promise<StartMessageResult> {
    const url = `${this.baseUrl}/${organigramId}/requests/start_message`;
    const body = { entry_node_id: entryNodeId, user_message: userMessage, session_id: sessionId || '' };
    return this.http.post<StartMessageResult>(url, body, { headers: this.buildHeaders(true) }).toPromise();
  }

  // GET /organigram/{id}/requests/{request_id}/traces  - the message trace of a request.
  // SNAPSHOT ONLY - not for UI polling. The live view listens to the organigram_process collection
  // directly (see processCollectionPath); polling this at UI rates costs ~2 + N + N*(1 + M) reads.
  getTraces(organigramId: string, requestId: string): Promise<RequestTrace> {
    const url = `${this.baseUrl}/${organigramId}/requests/${requestId}/traces`;
    return this.http.get<RequestTrace>(url, { headers: this.buildHeaders(true) }).toPromise();
  }

  // GET /organigram/{id}/requests/{request_id}  - the request document (status, etc.).
  getRequest(organigramId: string, requestId: string): Promise<OrganigramRequest> {
    const url = `${this.baseUrl}/${organigramId}/requests/${requestId}`;
    return this.http.get<OrganigramRequest>(url, { headers: this.buildHeaders(true) }).toPromise();
  }

  // GET /organigram/{id}/requests/traces  - every still-in-progress request's trace at once.
  // SNAPSHOT ONLY - same caveat as getTraces: the live overlay uses the Firestore listener instead.
  getAllTraces(organigramId: string): Promise<AllTraces> {
    const url = `${this.baseUrl}/${organigramId}/requests/traces`;
    return this.http.get<AllTraces>(url, { headers: this.buildHeaders(true) }).toPromise();
  }

  // GET /organigram/{id}/requests/pending  - actions awaiting THIS user's confirmation.
  // The authoritative source of confirmable execution ids: each item's `execution_id` is the
  // HUMAN_ESCALATION task, the only id confirmHumanAction accepts (see there).
  getPendingConfirmations(organigramId: string): Promise<PendingConfirmation[]> {
    const url = `${this.baseUrl}/${organigramId}/requests/pending`;
    return this.http.get<any>(url, { headers: this.buildHeaders(true) })
      .toPromise()
      .then(res => toArray<PendingConfirmation>(res));
  }

  // GET /organigram/{id}/requests/waiting_for_confirmation  - this user's own requests that are
  // blocked on SOMEONE ELSE's approval (the mirror image of getPendingConfirmations).
  getWaitingForConfirmation(organigramId: string): Promise<WaitingRequest[]> {
    const url = `${this.baseUrl}/${organigramId}/requests/waiting_for_confirmation`;
    return this.http.get<any>(url, { headers: this.buildHeaders(true) })
      .toPromise()
      .then(res => toArray<WaitingRequest>(res));
  }

  // ---- Chat sessions -----------------------------------------------------------------------
  // A session is one continuing conversation with a node: pass its id back into startMessage and
  // the backend replays that routing context. These endpoints are the backend-owned history the
  // chat dock's localStorage store stood in for.

  // GET /organigram/{id}/sessions  - this user's sessions across all nodes, newest first.
  listSessions(organigramId: string): Promise<OrganigramSessionSummary[]> {
    const url = `${this.baseUrl}/${organigramId}/sessions`;
    return this.http.get<any>(url, { headers: this.buildHeaders(true) })
      .toPromise()
      .then(res => toArray<OrganigramSessionSummary>(res));
  }

  // GET /organigram/{id}/sessions/{node_id}  - this user's sessions with ONE node, newest first.
  listNodeSessions(organigramId: string, nodeId: string): Promise<OrganigramSessionSummary[]> {
    const url = `${this.baseUrl}/${organigramId}/sessions/${nodeId}`;
    return this.http.get<any>(url, { headers: this.buildHeaders(true) })
      .toPromise()
      .then(res => toArray<OrganigramSessionSummary>(res));
  }

  // GET /organigram/{id}/sessions/{node_id}/{session_id}  - one session's full chat history.
  // 404 unless the session belongs to this user.
  getSessionHistory(organigramId: string, nodeId: string, sessionId: string): Promise<OrganigramSessionHistory> {
    const url = `${this.baseUrl}/${organigramId}/sessions/${nodeId}/${sessionId}`;
    return this.http.get<OrganigramSessionHistory>(url, { headers: this.buildHeaders(true) }).toPromise();
  }

  // GET /sessions  - this user's chat sessions across ALL organigrams in scope, newest first (a
  // root-level aggregate, not under /organigram and not tied to one company). Each item carries its
  // own organigram_id, so the caller can label/route it. Use this for a unified chat history that
  // isn't scoped to the selected org - the per-org listSessions above misses chats filed elsewhere.
  listAllSessions(): Promise<OrganigramSessionSummary[]> {
    const url = `${this.rootUrl}/sessions`;
    return this.http.get<any>(url, { headers: this.buildHeaders(true) })
      .toPromise()
      .then(res => toArray<OrganigramSessionSummary>(res));
  }

  // GET /pending  - actions awaiting this user's confirmation across ALL organigrams in scope (the
  // root-level counterpart of getPendingConfirmations). Each item carries organigram_id but NOT
  // node_id; its execution_id is the HUMAN_ESCALATION task - the only id confirmHumanAction accepts.
  getAllPendingConfirmations(): Promise<PendingConfirmation[]> {
    const url = `${this.rootUrl}/pending`;
    return this.http.get<any>(url, { headers: this.buildHeaders(true) })
      .toPromise()
      .then(res => toArray<PendingConfirmation>(res));
  }

  // Firestore path of the execution feed the live views subscribe to (onSnapshot), NOT an HTTP
  // route. Single-tenant layout, which is what this project's app2 database uses; a multi-tenant
  // deployment would prefix `accounts/{account_id}/`.
  //
  // '' when there is no organigram, rather than a path with an empty segment. Firestore rejects
  // `organigram//organigram_process` by throwing out of db.collection(), so an account that has not
  // created an organigram yet - or one mid-switch, before the id arrives - took an exception on
  // every listener attach. Returning a falsy path lets each caller skip instead, which is the honest
  // answer: there is no collection to listen to.
  processCollectionPath(organigramId: string): string {
    if (!(organigramId || '').trim()) { return ''; }
    return `organigram/${organigramId}/organigram_process`;
  }

  // Same layout as processCollectionPath: the request documents the Audit Logs table reads.
  // '' when there is no organigram, for the same reason - Firestore rejects an empty path segment.
  requestCollectionPath(organigramId: string): string {
    if (!(organigramId || '').trim()) { return ''; }
    return `organigram/${organigramId}/organigram_request`;
  }

  // Fired trigger executions for the Trigger Logs table. Same empty-id rule as the other paths.
  triggerLogsCollectionPath(organigramId: string): string {
    if (!(organigramId || '').trim()) { return ''; }
    return `organigram/${organigramId}/trigger_logs`;
  }

  // Cron/webhook run documents whose workflow_steps the Triggers detail pane reads.
  // Same empty-id rule as the other paths.
  workflowRunsCollectionPath(organigramId: string): string {
    if (!(organigramId || '').trim()) { return ''; }
    return `organigram/${organigramId}/workflow_runs`;
  }

  // ---- Trigger nodes ------------------------------------------------------------------------

  // Create a trigger: a STANDALONE workflow entry point. It takes no parent_id and is exempt from
  // both the "first node becomes the root" and "every later node needs a parent" rules - createNode
  // already omits a falsy parent_id, so passing none is enough.
  //
  // The identity is the trigger's own, not the caller's: a scheduler fires this node later with no
  // user attached, and the backend attributes the whole run to the user_id/account_id stored here.
  // That makes them required rather than incidental - a trigger with a blank user_id would run as
  // nobody. `allow_sensitive_actions` defaults to FALSE: true lets agents in the run act without
  // human confirmation, which is exactly what an unattended run needs and exactly what should never
  // be switched on by omission.
  // `config` is the trigger's schedule, message and example payload. TriggerData does NOT declare
  // these YET - the contract is still being written and they are coming - so today the backend
  // ignores them (Pydantic drops undeclared keys silently) and they persist the day the fields land,
  // with no change here. Sent rather than withheld for exactly the reason toBackendNodeWrite passes
  // unknown keys through: withholding them makes this a field whitelist, and the adoption day
  // becomes a silent no-op nobody notices.
  //
  // Spread FIRST so the identity below cannot be overwritten by a config key - the whole run hangs
  // off user_id, and a form field colliding with it must not be able to reassign who a trigger acts
  // as.
  async createTriggerNode(
    organigramId: string,
    name: string,
    options: { allowSensitiveActions?: boolean; config?: Record<string, any> } = {}
  //
  // Returns the `data` it wrote as well as the id, because a caller that PATCHes afterwards MUST
  // merge into it: a PATCH carrying `data` replaces the object wholesale, so a follow-up write built
  // from anything less deletes the identity this method just established.
  ): Promise<{ id: string; data: Record<string, any> }> {
    // Identity via the shared resolver (see resolveUserId): without it the create throws here and the
    // trigger can never be made on /organigram, where customerDB's id is blank.
    const user_id = this.resolveUserId();
    if (!user_id) { throw new Error('a trigger needs a signed-in user to run as'); }
    const body = {
      name,
      description: '',
      goals: '',
      type: 'trigger' as const,
      data: {
        ...(options.config || {}),
        // Required by the contract on create. TEMPORARY - see newWorkflowId; it references nothing
        // and is on its way out. Minted here rather than by the caller so every trigger gets one
        // whichever screen made it.
        workflow_id: newWorkflowId(),
        user_id,
        account_id: this.resolveAccountId(),
        allow_sensitive_actions: options.allowSensitiveActions === true
      }
    };
    // TEMPORARY - remove once the trigger payload is confirmed. Prints exactly what goes over the
    // wire. If this line does not appear in the console on a save, the running bundle predates the
    // change that put the config on the node, and the network tab is showing the OLD body.
    const created = await this.createNode(organigramId, body);
    return { id: created.id, data: body.data };
  }

  // Trigger nodes are ordinary BACKEND nodes - a trigger read/write goes straight to the backend, same as
  // any other node. (There used to be a Firestore "mirror" of every trigger under organigram_workflow_trial;
  // it was removed 2026-08-19 as redundant - the node already lives in the backend. These two methods are
  // kept as trigger-named passthroughs so their call sites still read as trigger-specific.)
  getTriggerNode(organigramId: string, nodeId: string): Promise<OrganigramNode> {
    return this.getNode(organigramId, nodeId);
  }

  updateTriggerNode(organigramId: string, nodeId: string, patch: UpdateNodeBody): Promise<void> {
    return this.updateNode(organigramId, nodeId, patch);
  }

  // ---- Pipeline nodes -----------------------------------------------------------------------

  // Create a pipeline node: a POINTER to a data pipeline that already exists (created through
  // DataPipelineService against the data-pipeline backend). PipelineData is exactly { pipeline_id },
  // so nothing about the pipeline itself - its sources, schedule, target agent - is stored here; the
  // node is where the pipeline sits in the org chart, and data_pipelines/{pipeline_id} is what it is.
  //
  // Its parent is the TRIGGER whose workflow it is a step of - canPlaceUnder is where that rule lives;
  // this method trusts its caller for it and only refuses a missing parent. It needs no identity of
  // its own: the trigger's run carries one. `pipelineId` is required rather than defaulted, because a
  // node pointing at nothing is exactly the state this method exists to prevent.
  createPipelineNode(
    organigramId: string,
    options: { name: string; parentId: string; pipelineId: string; description?: string }
  ): Promise<{ id: string }> {
    if (!options.pipelineId) { throw new Error('a pipeline node needs the pipeline it stands for'); }
    if (!options.parentId) { throw new Error('a pipeline node needs a parent'); }
    return this.createNode(organigramId, {
      parent_id: options.parentId,
      name: options.name,
      description: options.description || '',
      goals: '',
      type: 'pipeline',
      data: { pipeline_id: options.pipelineId }
    });
  }

  // ---- Workflow connections (Firestore-only) ------------------------------------------------
  // A workflow's nodes are REAL backend nodes (children of the trigger, created via createNode) so the
  // runner can start_message them. What lives in Firestore is only the FLOW between them: a CONNECTION doc
  // {source, target} at organigram/{id}/organigram_workflow_trial/{connId}. It is a frontend link only -
  // connecting two nodes never reparents in the backend, so the backend graph and its fan-out are untouched.
  // The canvas draws these as arrows ON TOP of the nodes (the nodes stay inside the trigger frame).
  private static readonly TRIGGER_MIRROR_COLLECTION = 'organigram_workflow_trial';

  private triggerMirrorCollection(organigramId: string): firebase.firestore.CollectionReference | null {
    const db = this.customerDB.app2?.firestore;
    if (!db || !organigramId) { return null; }
    return db.collection('organigram').doc(organigramId)
      .collection(OrganigramApiService.TRIGGER_MIRROR_COLLECTION);
  }

  // ---- A step's last real answer ------------------------------------------------------------
  //
  // The most recent output a step actually produced, for the pipeline wizard's "What arrives" box. A
  // step that declares an answer FORMAT gives the wizard its field names for free; one that answers in
  // free text - which is most of them - gives it nothing, and the author is left pasting an example by
  // hand. Its last real answer is the example, and it is already sitting in the run log.
  //
  // Reads the newest runs and asks each for THIS step's document (the doc id IS the node id, so no
  // query and no index), newest first, and stops at the first one that actually answered. Bounded to a
  // handful of runs: this is a convenience, not a search - a step that has not run recently simply has
  // nothing to offer, and the wizard says so.
  async lastStepOutput(
    organigramId: string, nodeId: string, scanRuns = 8
  ): Promise<{ output: any; runId: string; atMs: number } | null> {
    const db = this.customerDB.app2?.firestore;
    if (!db || !organigramId || !nodeId) { return null; }
    const runs = db.collection('organigram').doc(organigramId).collection('workflow_runs');
    let snap: any;
    try {
      snap = await runs.orderBy('created_at', 'desc').limit(scanRuns).get();
    } catch {
      // No index / no permission on the ordered read: an unordered page is still better than nothing,
      // and the sort below puts whatever came back in the right order anyway.
      try { snap = await runs.limit(scanRuns).get(); } catch { return null; }
    }
    const ids: string[] = (snap?.docs || []).map((d: any) => d.id);
    if (!ids.length) { return null; }
    const found = await Promise.all(ids.map(async runId => {
      try {
        const doc = await runs.doc(runId).collection('workflow_steps').doc(nodeId).get();
        const data = doc?.exists ? (doc.data() || {}) : null;
        if (!data || data['output'] === undefined || data['output'] === null || data['output'] === '') { return null; }
        const at = data['finished_at'] ?? data['created_at'] ?? null;
        const atMs = at && typeof at.toMillis === 'function' ? at.toMillis()
          : (at && at.seconds ? at.seconds * 1000 : 0);
        return { output: data['output'], runId, atMs };
      } catch { return null; }
    }));
    const hits = found.filter(h => !!h) as { output: any; runId: string; atMs: number }[];
    if (!hits.length) { return null; }
    // Newest answer wins. Falls back on the query order when the timestamps are missing.
    hits.sort((a, b) => b.atMs - a.atMs);
    return hits[0];
  }

  // Write a workflow connection (source flows to target) with a client-minted id. NOT best-effort: this is
  // a wiring the user asked for, so a denied/failed write THROWS and the caller reports it. It never
  // touches the backend - the nodes already exist there; only the frontend link is recorded.
  async createWorkflowConnection(
    organigramId: string, source: string, target: string,
    routing?: { if?: any; else?: boolean; for_each?: ForEachSpec; repeat_until?: RepeatUntilSpec }
  ): Promise<{ id: string }> {
    const col = this.triggerMirrorCollection(organigramId);
    if (!col) { throw new Error('the customer database has not connected yet'); }
    const ref = col.doc();   // mints an id without writing
    await ref.set({
      id: ref.id,
      source,
      target,
      ...(routing && routing.if !== undefined && routing.if !== null ? { if: routing.if } : {}),
      ...(routing && routing.else === true ? { else: true } : {}),
      // One repeat or the other, never both - the engine refuses an edge carrying the two, so a caller
      // that passed both would write a doc that fails the whole run at plan time. `for_each` wins here
      // only because something has to; the editor never offers the pair.
      ...(forEachOf(routing) ? { for_each: forEachOf(routing) }
        : repeatUntilOf(routing) ? { repeat_until: repeatUntilOf(routing) } : {}),
      updated_at: firebase.firestore.FieldValue.serverTimestamp()
    });
    return { id: ref.id };
  }

  // Every connection doc in the collection, for the canvas merge. A connection is the only doc that carries
  // both `source` and `target`; the trigger mirror docs sharing the collection have `type`/`name`/`data`
  // and no source, so filtering on source+target picks out exactly the connections.
  async listWorkflowConnections(organigramId: string): Promise<WorkflowConnection[]> {
    const col = this.triggerMirrorCollection(organigramId);
    if (!col) { return []; }
    const snap = await col.get();
    return snap.docs
      .map(doc => (doc.data() || {}) as any)
      .filter(d => d && d.source && d.target)
      // `if`/`else` and BOTH repeats are carried through, not dropped: they are the routing the runner
      // reads off the edge. This map is an ALLOWLIST - a key missing from it never reaches the canvas at
      // all, so a routing key added to the doc and forgotten here is invisible rather than broken. Only
      // the shape that exists is included, so a plain edge stays `{id, source, target}` and never grows
      // an empty `if`. A half-written repeat (no `items`, no `until`) is dropped rather than handed on.
      .map(d => ({
        id: d.id, source: d.source, target: d.target,
        ...(d.if !== undefined && d.if !== null ? { if: d.if } : {}),
        ...(d.else === true ? { else: true } : {}),
        ...(forEachOf(d) ? { for_each: forEachOf(d) as ForEachSpec } : {}),
        ...(repeatUntilOf(d) ? { repeat_until: repeatUntilOf(d) as RepeatUntilSpec } : {})
      }));
  }

  // Append a node to a trigger's workflow chain: one connection from the chain's current tail (the
  // trigger itself while it has no steps) to the node - the same append node-detail.saveForm does for
  // an agent step created under a trigger. The pipeline wizard cannot see the canvas model, so the tail
  // is read off the connection docs here (chainTail is GraphModelService.workflowChainTail's walk).
  // THROWS like createWorkflowConnection does: the caller decides what an unlinked step means.
  //
  // afterStepId names the step the new one should FOLLOW, when the user pressed the "+" on a step
  // card rather than on the trigger. The chain tail stays the fallback, so the trigger's own "+" is
  // unchanged. Attaching to a step that already has a successor forks the flow rather than splicing
  // into it - both branches then run on that step's answer, which is what an outbound pipeline
  // (a delivery leaf) wants.
  async appendWorkflowStep(
    organigramId: string, triggerId: string, nodeId: string, afterStepId?: string | null
  ): Promise<void> {
    const connections = await this.listWorkflowConnections(organigramId);
    await this.createWorkflowConnection(
      organigramId, this.attachPoint(connections, triggerId, afterStepId), nodeId
    );
  }

  // The one rule both helpers below follow, so they can never disagree about what a new step follows.
  // An afterStepId is honoured only when the chain can actually reach it: a stale id from another
  // workflow (or a step deleted meanwhile) falls back to the tail instead of writing an orphan edge.
  private attachPoint(
    connections: { source: string; target: string }[], triggerId: string, afterStepId?: string | null
  ): string {
    if (!afterStepId) { return chainTail(connections, triggerId); }
    const reachable = new Set<string>([triggerId]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const c of connections) {
        if (reachable.has(c.source) && !reachable.has(c.target)) { reachable.add(c.target); grew = true; }
      }
    }
    return reachable.has(afterStepId) ? afterStepId : chainTail(connections, triggerId);
  }

  // The step a NEW step would follow: the chain's current tail as a full node, or null while the
  // chain is empty (the new step follows the trigger itself). Read by the pipeline wizard, which
  // takes an agent step's agent as the outbound pipeline's source agent. Same walk as
  // appendWorkflowStep, so the two agree on what "last" means.
  async workflowStepBefore(
    organigramId: string, triggerId: string, afterStepId?: string | null
  ): Promise<OrganigramNode | null> {
    const at = this.attachPoint(await this.listWorkflowConnections(organigramId), triggerId, afterStepId);
    if (at === triggerId) { return null; }
    // The id is GUARANTEED here, not trusted from the payload: GET /nodes/{id} comes back without one
    // in practice, whatever OrganigramNode declares, and a caller that needs to ask anything else about
    // this node (its run log, its documents) has no way to notice a silently undefined id.
    const node = await this.getNode(organigramId, at);
    return node ? { ...node, id: node.id || at } : node;
  }

  // A patch on ONE connection's routing, addressed by id so the source/target - and the arrow already
  // drawn - are left where they are. Only the keys PRESENT are touched; an omitted key is left exactly
  // as it is, and null clears. That opt-in shape is what lets `if` and `for_each` be edited
  // independently: an edge can carry BOTH (gate first, then fan out), so a gate edit must never wipe
  // the repeat and a repeat edit must never wipe the gate.
  //
  // update(), NOT set({merge:true}): a merge write DEEP-merges nested maps, so a partial rule would
  // leave the previous one's keys behind - the bug that already bites `if` today (switching eq -> ne
  // stores {'==':[...], '!=':[...]}, after which parseIfRule refuses it and the arrow is a permanently
  // uneditable "if (custom rule)"). update() replaces a named field wholesale, and refuses a missing
  // doc instead of creating a headless {if, updated_at} one that listWorkflowConnections then filters
  // out. THROWS on failure - it is an edit the user asked for.
  async updateWorkflowConnection(
    organigramId: string, connectionId: string, patch: ConnectionPatch
  ): Promise<void> {
    const col = this.triggerMirrorCollection(organigramId);
    if (!col || !connectionId) { throw new Error('the customer database has not connected yet'); }
    const del = firebase.firestore.FieldValue.delete();
    const body: any = { updated_at: firebase.firestore.FieldValue.serverTimestamp() };
    if ('if' in patch)       { body.if = patch.if === undefined || patch.if === null ? del : patch.if; }
    if ('else' in patch)     { body.else = patch.else === true ? true : del; }
    // THE mutual-exclusivity gate. Setting either repeat clears the other IN THE SAME WRITE, because
    // this single update() is the only place one atomic write sees both keys - an editor guard alone
    // leaves the pair authorable through a re-wire, a paste or a second tab, and the engine answers a
    // doc carrying both by failing the whole run at plan time. Clearing the sibling is safe when it was
    // already absent: deleting a field that does not exist is a no-op.
    if ('for_each' in patch) {
      body.for_each = patch.for_each ? patch.for_each : del;
      if (patch.for_each) { body.repeat_until = del; }
    }
    if ('repeat_until' in patch) {
      body.repeat_until = patch.repeat_until ? patch.repeat_until : del;
      if (patch.repeat_until) { body.for_each = del; }
    }
    await col.doc(connectionId).update(body);
  }

  // Wire source->target, REPLACING any existing incoming connection for target - a workflow node has a
  // single incoming flow, so re-connecting it moves the arrow rather than stacking a second one. Done in a
  // batch so the swap is atomic. Reads the whole (small) collection and filters client-side to avoid a
  // composite index on (kind, target). THROWS on failure - it is a wiring the user asked for.
  async setWorkflowConnection(organigramId: string, source: string, target: string): Promise<void> {
    const col = this.triggerMirrorCollection(organigramId);
    if (!col) { throw new Error('the customer database has not connected yet'); }
    const snap = await col.get();
    const stale = snap.docs.filter(doc => {
      const d = (doc.data() || {}) as any;
      return d.source && d.target === target;   // an existing connection INTO this node
    });
    // A repeat belongs to the TARGET step (the backend requires that step to be reached by one arrow
    // only), so it survives a re-wire that moves which step feeds it. The gate does NOT: an `if` is a
    // decision the SOURCE makes about its own answer, and the source is exactly what changed. The
    // caller confirms the move and names the list first (repeatRewireConfirm) - this method never
    // throws over it, because a statusless throw here reaches the user as a false network error.
    // Carry off whichever stale doc actually HAS a repeat, rather than keying on there being exactly one.
    // The callers count arrows in the canvas model, which drops any connection whose endpoints are not
    // both live nodes - so an orphan doc left behind by a failed cleanup is invisible to them and visible
    // here. Keying on the count would silently delete the repeat under a confirm that promised to move it.
    // BOTH kinds ride across, for the same reason and with the same caveat. A repeat belongs to the
    // target, and `repeat_until` belongs to it more strongly still: its rule is judged against the
    // BODY's answer, so changing which step feeds the loop does not move what the rule reads at all.
    // Whichever kind the stale doc carried is the one re-minted - never both, since no doc may hold both.
    const carriedFor = stale.map(doc => forEachOf(doc.data() || {})).find(f => !!f);
    const carriedUntil = carriedFor ? null : stale.map(doc => repeatUntilOf(doc.data() || {})).find(u => !!u);
    const batch = col.firestore.batch();
    for (const doc of stale) { batch.delete(doc.ref); }
    const ref = col.doc();
    batch.set(ref, {
      id: ref.id, source, target,
      ...(carriedFor ? { for_each: carriedFor } : {}),
      ...(carriedUntil ? { repeat_until: carriedUntil } : {}),
      updated_at: firebase.firestore.FieldValue.serverTimestamp()
    });
    await batch.commit();
  }

  // Delete one connection doc by id. Best-effort caller usage (cleanup after a node delete), so it just
  // removes the doc; a missing doc is a no-op delete.
  async deleteWorkflowConnection(organigramId: string, connectionId: string): Promise<void> {
    const col = this.triggerMirrorCollection(organigramId);
    if (!col || !connectionId) { return; }
    await col.doc(connectionId).delete();
  }

  // POST /organigram/{id}/human_confirmation/{execution_id}  - resume an execution paused waiting for a
  // human decision. `approved` = true approves, false rejects (one route carries both). Requires
  // X-User-Id. NOTE: NO trailing /confirm - the backend route is exactly .../human_confirmation/{id}.
  //
  // WHICH execution_id: the HUMAN_ESCALATION task on the human node - the item the human actually
  // sees, i.e. an execution whose `mode` is 'human_escalation'. Two executions sit in
  // 'waiting_for_human' at once; posting the AGENT's own execution id (the one that raised the
  // interrupt) fails with 400 "... is not a human confirmation task". The backend resumes that agent
  // itself via the task's parent_execution_id. Get the right id from getPendingConfirmations, or
  // from the process listener by filtering on mode.
  confirmHumanAction(organigramId: string, executionId: string, approved: boolean): Promise<HumanConfirmationResult> {
    const url = `${this.baseUrl}/${organigramId}/human_confirmation/${executionId}`;
    return this.http.post<HumanConfirmationResult>(url, { approved }, { headers: this.buildHeaders(true) }).toPromise();
  }

}

// Collection endpoints are documented as returning a list, but tolerate the common wrapper shapes
// so a backend that switches to `{items: [...]}` does not crash the views.
function toArray<T>(res: any): T[] {
  if (Array.isArray(res)) { return res as T[]; }
  for (const key of ['items', 'results', 'pending', 'requests', 'sessions', 'data']) {
    if (Array.isArray(res?.[key])) { return res[key] as T[]; }
  }
  return [];
}

// Result of POST .../requests/start_message. `session_id` is the session the request joined -
// the one that was passed in, or a freshly minted one. Carry it into the next startMessage.
export interface StartMessageResult {
  request_id: string;
  session_id?: string;
}

// One action awaiting this user's approval (GET /requests/pending, and the cross-organigram
// GET /pending - both return this shape). `execution_id` is the HUMAN_ESCALATION task, the id to
// post to confirmHumanAction. Every field is required by the backend.
//
// There is NO node_id here, on either route: the item names the request and the organigram, not the
// node the task sits on. Callers label the card from `message` / `user_message`, or resolve the node
// from the live process feed - not from this response.
export interface PendingConfirmation {
  execution_id: string;
  request_id: string;
  organigram_id: string;
  user_message: string;   // the original question that led to this action
  message: string;        // the confirmation prompt to show the user
  [key: string]: any;
}

// One of this user's requests blocked on someone else's approval
// (GET /requests/waiting_for_confirmation) - whole Request documents, same shape as getRequest.
export type WaitingRequest = OrganigramRequest;

// A chat session summary (GET /sessions, /sessions/{node_id}, and the cross-organigram GET /sessions).
// Only `session_id` is guaranteed; the other three default to '' server-side. The timestamp is
// `updatedISO`, an ISO string - this shape carries no created_at/updated_at and no title.
export interface OrganigramSessionSummary {
  session_id: string;
  organigram_id?: string;
  node_id?: string;
  updatedISO?: string;
  [key: string]: any;
}

// One message in a session's history. `role` is 'user' or 'model' - the backend's word for the
// assistant side - and the body is `text`, NOT `content`.
export interface OrganigramSessionMessage {
  role: 'user' | 'model' | string;
  text: string;
  [key: string]: any;
}

// One session's full history (GET /sessions/{node_id}/{session_id}); 404 unless it belongs to the
// calling user.
export interface OrganigramSessionHistory {
  session_id?: string;
  node_id?: string;
  user_id?: string;
  createdISO?: string;
  updatedISO?: string;
  messages?: OrganigramSessionMessage[];
  [key: string]: any;
}

// One node-visit document in the organigram_process collection - what the live listeners read.
// The edge to animate is sender_node_id -> node_id; `mode` distinguishes a HUMAN_ESCALATION task
// (confirmable) from the agent execution that raised it (not confirmable).
export interface OrganigramProcess {
  id: string;
  request_id?: string;
  user_id?: string;
  parent_execution_id?: string;
  node_id?: string;
  sender_node_id?: string;
  sender_type?: 'user' | 'node' | string;
  message?: string;
  mode?: 'delegate' | 'local_only' | 'human_escalation' | string;
  status?: string;
  response?: string;
  error?: string;
  created_at?: any;
  [key: string]: any;
}

// Result of POST .../human_confirmation/{execution_id}. `status` is the execution's state
// after the decision: completed, still waiting_for_human, or failed.
export interface HumanConfirmationResult {
  execution_id: string;
  status: 'completed' | 'waiting_for_human' | 'failed' | string;
  response?: string;
  error?: string;
}

export interface TraceProcess {
  source_node_id: string;
  recipient_node_id: string;
  message: string;
}

export interface RequestTrace {
  processes: TraceProcess[];
  request_id: string;
}

// One live request in the "all traces" feed: its trace plus enough to summarise/route it.
export interface TraceEntry {
  request_id: string;
  status?: string;
  entry_node_id?: string;
  processes: TraceProcess[];
}

export interface AllTraces {
  traces: TraceEntry[];
}

// The Request document (GET /requests/{request_id}). NOTE: it does not carry its own id - the
// request id is the one you asked with, not a field on the body. `response` holds the final answer
// and is only meaningful once `completed_at` is set or `status` is terminal.
export interface OrganigramRequest {
  entry_node_id?: string;
  session_id?: string;
  account_id?: string;
  user_id?: string;
  organigram_id?: string;
  user_message?: string;
  status?: 'in_progress' | 'completed' | 'failed' | string;
  root_execution_id?: string;
  response?: string;
  created_at?: any;
  completed_at?: any;
  [key: string]: any;
}

// `type` and `data` are the UI's own model (department / division are node types here). createNode
// and updateNode translate them for the backend - see organigram-node-write.ts.
//
// The write bodies are typed OrgWriteNodeType rather than OrganigramNodeType because the two unions
// are no longer identical in spirit: OrgWriteNodeType is what a create/update may carry, and it is
// where a type the backend stops accepting would be removed from first.
export interface CreateNodeBody {
  parent_id?: string | null;
  name: string;
  description: string;
  goals: string;
  type: OrgWriteNodeType;
  data: Record<string, any>;
}

export interface UpdateNodeBody {
  name?: string;
  description?: string;
  goals?: string;
  type?: OrgWriteNodeType;
  data?: Record<string, any>;
}

// The company metadata the organigram document stores, shared by the response and both write
// bodies. All optional and nullable: organigrams written before the backend took a given field
// carry no value at all, so absent and null both mean "not set".
export interface OrganigramCompanyFields {
  goals?: string | null;
  description?: string | null;
  // Operating principles the company works by.
  guidelines?: string | null;
  // Free-form background: history, culture, market, products.
  extended_context?: string | null;
  // Separate from `headquarters` on the backend - do not map one onto the other.
  location?: string | null;
  website?: string | null;
  industry?: string | null;
  // Headcount as text ("500-1000"), not a number.
  employees?: string | null;
  founded?: string | null;
  revenue?: string | null;
  headquarters?: string | null;
}

// The organigram (company) document returned by every /organigram endpoint. `team_count` is
// computed per request (number of type == 'team' nodes: divisions, departments and teams), not stored.
export interface OrganigramResponse extends OrganigramCompanyFields {
  id: string;
  name: string;
  team_count: number;
  // Server-managed soft-delete flag, set by DELETE /organigram/{id} and not writable through create
  // or update. Archived organigrams are filtered out of listOrganigrams, so this is only ever seen
  // on a document fetched by id.
  archived?: boolean;
}

// Body for POST /organigram. Only `name` is required (must not be blank). `id` is optional:
// a non-blank value becomes the document id, blank/whitespace/omitted lets the backend generate one.
export interface CreateOrganigramBody extends OrganigramCompanyFields {
  name: string;
  id?: string;
}

// Body for PATCH /organigram/{id}. Every field is optional and only the ones PRESENT are written:
// an omitted key leaves the stored value alone, an explicit null clears it. The document id is
// immutable, which is why it is not here - a rename changes `name`, never the id.
export interface UpdateOrganigramBody extends OrganigramCompanyFields {
  name?: string | null;
}
